/**
 * UdoPaintsEditor Integration Utility
 * Provides a clean interface for interacting with the UdoPaintsEditor app block
 */

export const SKIP_APP_BLOCK = '__UDOPAINTS_EDITOR_SKIP_APP_BLOCK__';
const UDOPAINTS_EDITOR_REGISTERED_TIMEOUT_MS = 2000;

class UdoPaintsEditorManager {

  /** @type {boolean} */
  isReady;

  /** @type {boolean} */
  isTimeoutReached;

  /** @type {(() => void)[]} */
  readyCallbacks;

  /** @type {(() => void)[]} */
  failureCallbacks;

  constructor() {
    this.isReady = false;
    this.isTimeoutReached = false;
    this.readyCallbacks = [];
    this.failureCallbacks = [];
    this.checkAvailability();
  }

  /**
   * Check if UdoPaintsEditor is available
   */
  checkAvailability() {
    const startTime = Date.now();
    const check = () => {
      if (window.UdoPaintsEditor) {
        this.isReady = true;
        this.readyCallbacks.forEach(callback => callback());
        this.readyCallbacks = [];
      } else if (Date.now() - startTime > UDOPAINTS_EDITOR_REGISTERED_TIMEOUT_MS) {
        console.warn('UdoPaintsEditorManager: UdoPaintsEditor not available after timeout.');
        this.isTimeoutReached = true;
        this.failureCallbacks.forEach(callback => callback());
        this.failureCallbacks = [];
      } else {
        // Check again after a short delay
        setTimeout(check, 100);
      }
    }
    check();
  }

  /**
   * Wait for UdoPaintsEditor to be ready
   * @param {() => void} callback - Callback to execute when ready
  */
  onReady(callback) {
    if (this.isReady) {
      callback();
    } else {
      this.readyCallbacks.push(callback);
    }
  }

  /**
   * Wait for timeout to be reached
   * @param {() => void} callback - Callback to execute on timeout
   */
  onTimeout(callback) {
    if (this.isTimeoutReached) {
      callback();
    } else {
      this.failureCallbacks.push(callback);
    }
  }

  /**
   * Call onAddToCart method
   * @param {object} params - The parameters object
   * @param {string} params.productId - The product ID
   * @param {string} params.variantId - The variant ID
   * @returns {Promise<any>} Promise with result object
   */
  async onAddToCart(params) {
    return new Promise((resolve) => {
      if (this.isTimeoutReached) {
        resolve({
          success: false,
          error: SKIP_APP_BLOCK
        });
        return;
      }
      this.onReady(async () => {
        try {
          const result = await window.UdoPaintsEditor.onAddToCart(params);
          resolve(result);
        } catch (error) {
          resolve({
            success: false,
            error: error.message || 'Failed to call onAddToCart'
          });
        }
      });
    });
  }

  /**
   * Call afterAddToCart method
   * @param {object} params - The parameters object
   * @param {string} params.itemKey - The cart item key
   * @param {string} params.cartToken - The cart token
   * @returns {Promise<any>} Promise with result object
   */
  async afterAddToCart(params) {
    return new Promise((resolve) => {
      if (this.isTimeoutReached) {
        resolve({
          success: false,
          error: SKIP_APP_BLOCK
        });
        return;
      }
      this.onReady(async () => {
        try {
          const result = await window.UdoPaintsEditor.afterAddToCart(params);
          resolve(result);
        } catch (error) {
          resolve({
            success: false,
            error: error.message || 'Failed to call afterAddToCart'
          });
        }
      });
    });
  }

  /**
   * Call isReadyToExport method
   * @returns {Promise<any>} Promise with result object
   */
  async isReadyToExport() {
    return new Promise((resolve) => {
      if (this.isTimeoutReached) {
        resolve({
          success: false,
          error: SKIP_APP_BLOCK
        });
        return;
      }
      this.onReady(async () => {
        try {
          const result = await window.UdoPaintsEditor.isReadyToExport();
          resolve(result);
        } catch (error) {
          resolve({
            success: false,
            error: error.message || 'Failed to call isReadyToExport'
          });
        }
      });
    });
  }

  /**
   * Call onError method
   * @param {any} error - The error object
   * @returns {Promise<void>} Promise that resolves when the error is handled
   */
  async onError(error) {
    return new Promise((resolve) => {
      if (this.isTimeoutReached) return;
      this.onReady(async () => {
        try {
          await window.UdoPaintsEditor.onError(error);
          resolve();
        } catch (err) {
          console.error('Failed to call UdoPaintsEditor.onError:', err);
          resolve();
        }
      });
    });
  }

  /**
   * Call uploadImage method
   * @returns {Promise<{ success: boolean, feature: string, error?: string }>} Promise with result object
   */
  async uploadImage() {
    return new Promise((resolve) => {
      if (this.isTimeoutReached) {
        resolve({
          success: false,
          feature: '',
          error: SKIP_APP_BLOCK
        });
        return;
      }
      this.onReady(async () => {
        try {
          const result = await window.UdoPaintsEditor.uploadImage();
          resolve(result);
        } catch (error) {
          console.error('Failed to call UdoPaintsEditor.uploadImage:', error);
          resolve({
            success: false,
            feature: '',
            error: error.message || 'Failed to call uploadImage'
          });
        }
      });
    });
  }

  /**
   * Set up state change handler
   * @param {(params: any) => Promise<void> | void} handler - Function to handle state changes
   */
  onStateChange(handler) {
    if (this.isTimeoutReached) return;
    this.onReady(() => {
      try {
        window.UdoPaintsEditor.onStateChange(handler);
      } catch (error) {
        console.error('Failed to set UdoPaintsEditor.onStateChange:', error);
      }
    });
  }

  /**
   * Check if UdoPaintsEditor is available
   * @returns {boolean} True if available
   */
  isAvailable() {
    return this.isReady;
  }
}

// Create a global instance
const udopaintsEditor = new UdoPaintsEditorManager();

// Export for use in other modules
export { udopaintsEditor as UdoPaintsEditor };

// Also make it available globally for easy access
window.UdoPaintsEditorManager = udopaintsEditor;
